import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { MobileNavItem } from '../hooks/useMobileNavItems';

/**
 * The "More" overflow bottom sheet for the pillar-aware (customer-tier) mobile
 * nav. Shows the pillar/module items that didn't fit in the bottom bar, plus
 * account actions (log out). Everything here is already role/pillar-gated by
 * useMobileNavItems — this component just renders what it's given.
 */
interface MobileMoreSheetProps {
  open: boolean;
  items: MobileNavItem[];
  activePath: string;
  onClose: () => void;
  onLogout: () => void;
}

const PRIMARY = '#1a6b3c';
const DARK = '#0f2d1f';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const DANGER = '#d32f2f';

export function MobileMoreSheet({ open, items, activePath, onClose, onLogout }: MobileMoreSheetProps) {
  const navigate = useNavigate();
  if (!open) return null;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return createPortal(
    <div style={styles.overlay} onClick={onClose} role="presentation">
      <div
        style={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="More navigation"
      >
        <div style={styles.grabber} aria-hidden />
        <div style={styles.header}>
          <span style={styles.title}>More</span>
          <button style={styles.close} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {items.length > 0 && (
          <>
            <div style={styles.sectionLabel}>This workspace</div>
            <nav aria-label="More destinations">
              {items.map((item) => {
                const isActive =
                  activePath === item.path || activePath.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    style={{ ...styles.row, ...(isActive ? styles.rowActive : null) }}
                    onClick={() => go(item.path)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span style={styles.rowIcon} aria-hidden>
                      {item.icon}
                    </span>
                    <span style={styles.rowLabel}>{item.label}</span>
                  </button>
                );
              })}
            </nav>
            <div style={styles.divider} />
          </>
        )}

        <div style={styles.sectionLabel}>Account</div>
        <button style={{ ...styles.row, ...styles.logoutRow }} onClick={onLogout}>
          <span style={styles.rowIcon} aria-hidden>
            ⎋
          </span>
          <span style={{ ...styles.rowLabel, color: DANGER }}>Log out</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15,45,31,0.45)',
    zIndex: 2147483646, // just under the bottom nav
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'none',
  },
  sheet: {
    width: '100%',
    maxHeight: '70vh',
    overflowY: 'auto',
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: '10px 0 calc(76px + env(safe-area-inset-bottom)) 0',
    boxShadow: '0 -8px 30px rgba(0,0,0,0.25)',
  },
  grabber: {
    width: 40,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#d6dcd9',
    margin: '2px auto 10px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px 6px',
  },
  title: { fontSize: '1.1rem', fontWeight: 800, color: DARK },
  close: {
    background: 'none',
    border: 'none',
    fontSize: '1rem',
    color: MUTED,
    cursor: 'pointer',
    padding: 6,
    lineHeight: 1,
  },
  sectionLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#9ca3af',
    padding: '12px 20px 4px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 20px',
    background: 'none',
    border: 'none',
    borderBottom: `1px solid #f4f6f5`,
    cursor: 'pointer',
    textAlign: 'left',
  },
  rowActive: { backgroundColor: '#f0fdf4' },
  rowIcon: { fontSize: '1.25rem', width: 26, textAlign: 'center', lineHeight: 1 },
  rowLabel: { fontSize: '0.95rem', fontWeight: 600, color: DARK },
  divider: { height: 8, backgroundColor: '#f5f7f6', margin: '8px 0' },
  logoutRow: { borderBottom: 'none' },
};
